import { useState } from 'react';
import { Mail, Phone, Clock } from 'lucide-react';
import HeroBanner from '../components/HeroBanner';
import { siteInfo } from '../data';
import './ContactUs.css';

// Jurza public intake endpoint. Bridge LPO is a law firm, so enquiries belong
// in the practice management system, where each one lands as a lead the firm
// can convert — AcquireZa now serves the non-legal sites only.
//
// Unlike AcquireZa, the firm is named in the path rather than inferred from the
// Origin header: /api/public/intake/{tenant}/{form}/submit.
const CONTACT_API_BY_MODE = {
  development: 'https://dev-api.jurza.com/api/public/intake/bridge-lpo/contact/submit',
  dev: 'https://dev-api.jurza.com/api/public/intake/bridge-lpo/contact/submit',
  stage: 'https://stg-api.jurza.com/api/public/intake/bridge-lpo/contact/submit',
  prod: 'https://api.jurza.com/api/public/intake/bridge-lpo/contact/submit',
};

const CONTACT_API =
  import.meta.env.VITE_CONTACT_API_URL ||
  CONTACT_API_BY_MODE[import.meta.env.MODE] ||
  CONTACT_API_BY_MODE.development;

export default function ContactUs() {
  const [form, setForm] = useState({ fullName: '', email: '', message: '' });
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const response = await fetch(CONTACT_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          // Jurza matches these against the intake form's field keys, and
          // splits full_name into the client's first and last name.
          full_name: form.fullName,
          email: form.email,
          message: form.message,
        }),
      });

      const result = await response.json();

      // Jurza answers with { message, submission_id, redirect_url } and no
      // success flag, so the HTTP status is what tells us it worked.
      if (!response.ok) {
        throw new Error(result.message || 'Submission failed');
      }

      setStatus('success');
      setForm({ fullName: '', email: '', message: '' });
    } catch (err) {
      console.error('Contact form error:', err);
      setStatus('error');
    }
  };

  return (
    <>
      <HeroBanner
        title="Contact Us"
        breadcrumb="Contact Us"
      />

      <section className="contact-page">
        <div className="container">
          <div className="contact-page__grid">
            {/* Contact Info Cards */}
            <div className="contact-page__info">
              <div className="info-card">
                <div className="info-card__icon">
                  <Mail size={24} />
                </div>
                <h4>Email Us</h4>
                <a href={`mailto:${siteInfo.email}`}>{siteInfo.email}</a>
              </div>
              <div className="info-card">
                <div className="info-card__icon">
                  <Phone size={24} />
                </div>
                <h4>Call Us</h4>
                <a href={`tel:${siteInfo.phone}`}>{siteInfo.phoneDisplay}</a>
              </div>
              <div className="info-card">
                <div className="info-card__icon">
                  <Clock size={24} />
                </div>
                <h4>Business Hours</h4>
                <p>Mon - Fri: {siteInfo.businessHours.weekdays}</p>
                <p>Sat: {siteInfo.businessHours.saturday}</p>
                <p>Sun: {siteInfo.businessHours.sunday}</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-page__form">
              <h2>Send us a message</h2>
              <p className="contact-page__form-desc">
                Have a question or want to learn more about our services? Fill out the form
                below and we'll get back to you promptly.
              </p>

              {status === 'success' ? (
                <div className="form-success">
                  <h3>Message Sent!</h3>
                  <p>Thank you for reaching out. We'll respond within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <label htmlFor="c-name">Full Name *</label>
                    <input
                      id="c-name" name="fullName" type="text" required
                      value={form.fullName} onChange={handleChange}
                      placeholder="Your full name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="c-email">Email *</label>
                    <input
                      id="c-email" name="email" type="email" required
                      value={form.email} onChange={handleChange}
                      placeholder="your@email.com"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="c-message">Message</label>
                    <textarea
                      id="c-message" name="message" rows={6}
                      value={form.message} onChange={handleChange}
                      placeholder="How can we help you?"
                    />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={status === 'submitting'}>
                    {status === 'submitting' ? 'Sending...' : 'Submit'}
                  </button>
                  {status === 'error' && (
                    <p className="form-error">Something went wrong. Please try again.</p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
